import {BehaviorSubject, Observable, startWith, switchMap, tap} from 'rxjs';
import {CalendarEvent, RsvpResponse} from '../Models/CalendarEvent';
import {EventsService} from '../Network/EventsService';
import {loadingResponse, NetworkRequestStatus, NetworkResult} from '../Network/NetworkResult';
import {Duration} from '../Domain/Duration';
import {RecurrenceRule} from '../Models/ScheduledEvent';
import {ScheduledEventUpdateInput} from '../Models/ScheduledEventUpdateInput';

enum Event {
    Init,
    Create,
    Update,
    Delete,
    Rsvp,
}

export class EventsInteractor {
    private _events = new BehaviorSubject<Event>(Event.Init);
    private _calendarEvents = new BehaviorSubject<CalendarEvent[]>([]);

    public get calendarEvents(): Observable<CalendarEvent[]> {
        return this._calendarEvents;
    }

    public get(from: Date, till: Date): Observable<NetworkResult<CalendarEvent[]>> {
        return this._events.pipe(
            switchMap(() => this.eventsService.get(from, till)),
            //Make sure we have the latest events in the _calendarEvents global
            tap((result) => {
                if (result.status === NetworkRequestStatus.Success) {
                    this.broadcast(result.data);
                }
            }),
            startWith(loadingResponse()),
        );
    }

    constructor(private eventsService: EventsService) {}

    public async create(
        crewId: number,
        title: string,
        description: string,
        startAt: Date,
        duration: Duration,
        recurrenceRule: RecurrenceRule | null,
    ): Promise<NetworkResult<any>> {
        const result = await this.eventsService.createScheduledEvent(
            crewId,
            title,
            description,
            startAt,
            duration,
            recurrenceRule,
        );
        this._events.next(Event.Create);
        return result;
    }

    public async update(
        input: ScheduledEventUpdateInput,
        title: string,
        description: string,
        date: Date,
        duration: Duration,
    ): Promise<NetworkResult<any>> {
        const result = await this.eventsService.updateScheduledEvent(input, title, description, date, duration);
        this._events.next(Event.Update);
        return result;
    }

    public async delete(input: ScheduledEventUpdateInput): Promise<NetworkResult<any>> {
        const result = await this.eventsService.deleteScheduledEvent(input);
        this._calendarEvents.next([
            ...this._calendarEvents.value.filter((event) => {
                return event.scheduledEventId !== input.id;
            }),
        ]);
        this._events.next(Event.Delete);
        return result;
    }

    public async rsvp(
        scheduledEventId: number,
        userId: number,
        startsAt: Date,
        rsvpResponse: RsvpResponse,
    ): Promise<NetworkResult<any>> {
        const result = await this.eventsService.rsvpToCalendarEvent(scheduledEventId, userId, startsAt, rsvpResponse);
        this._events.next(Event.Rsvp);
        return result;
    }

    private broadcast(data: CalendarEvent[]): void {
        this._calendarEvents.next(
            [
                ...this._calendarEvents.value.filter((event) => {
                    return !data.some(
                        (newEvent: CalendarEvent) => newEvent.scheduledEventId === event.scheduledEventId,
                    );
                }),
                ...data,
            ].sort((a, b) => a.startAt.getTime() - b.startAt.getTime()),
        );
    }
}
